<template>
  <!--
    StorageDialog.vue
    Admin "Storage" dialog — consolidates every space-usage figure in one place:
      1. Server disk        (app server filesystem)
      2. Proxy disk         (SSH proxy filesystem)
      3. R2 — cache         (cleanup-eligible cached files)
      4. R2 — saved files   (pinned saved episodes, never cleaned)
    Fetches GET /admin/storage when opened. All sizes come back as raw bytes and
    are formatted here.
  -->
  <v-dialog
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    :fullscreen="mobile"
    max-width="460"
  >
    <v-card rounded="lg">
      <v-card-title class="d-flex align-center">
        <v-icon icon="mdi-harddisk" class="mr-2"></v-icon>
        <span>Storage</span>
        <v-spacer></v-spacer>
        <v-btn icon="mdi-refresh" variant="text" size="small" :disabled="loading" @click="load"></v-btn>
        <v-btn icon="mdi-close" variant="text" size="small" @click="$emit('update:modelValue', false)"></v-btn>
      </v-card-title>

      <v-card-text>
        <!-- Loading -->
        <div v-if="loading" class="text-center py-8">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>

        <!-- Error -->
        <div v-else-if="error" class="text-error py-4">{{ error }}</div>

        <template v-else-if="data">
          <!-- Disk rows: progress bar of used/total -->
          <div v-for="d in disks" :key="d.title" class="mb-4">
            <div class="d-flex align-center mb-1">
              <v-icon :icon="d.icon" size="small" class="mr-2"></v-icon>
              <span class="font-weight-medium">{{ d.title }}</span>
              <v-spacer></v-spacer>
              <span v-if="d.info" class="text-caption text-disabled">
                {{ fmt(d.info.usedBytes) }} / {{ fmt(d.info.totalBytes) }}
              </span>
              <span v-else class="text-caption text-disabled">unavailable</span>
            </div>
            <v-progress-linear
              v-if="d.info"
              :model-value="pct(d.info.usedBytes, d.info.totalBytes)"
              :color="barColor(pct(d.info.usedBytes, d.info.totalBytes))"
              height="8"
              rounded
            ></v-progress-linear>
          </div>

          <v-divider class="my-3"></v-divider>

          <!-- R2 rows: size + object count (no total to bar against) -->
          <div class="d-flex align-center mb-3">
            <v-icon icon="mdi-cloud-outline" size="small" class="mr-2"></v-icon>
            <span class="font-weight-medium">R2 — cache</span>
            <v-spacer></v-spacer>
            <span class="text-caption text-disabled">
              <template v-if="data.r2 && data.r2.enabled">{{ fmt(data.r2.cacheBytes) }} · {{ data.r2.cacheCount }} files</template>
              <template v-else>disabled</template>
            </span>
          </div>
          <div class="d-flex align-center">
            <v-icon icon="mdi-content-save" size="small" color="success" class="mr-2"></v-icon>
            <span class="font-weight-medium">R2 — saved files</span>
            <v-spacer></v-spacer>
            <span class="text-caption text-disabled">
              <template v-if="data.r2 && data.r2.enabled">{{ fmt(data.r2.savedBytes) }} · {{ data.r2.savedCount }} files</template>
              <template v-else>disabled</template>
            </span>
          </div>
          <!-- Of the saved files, how much sits in the Archivio podcast -->
          <div v-if="data.r2 && data.r2.enabled" class="d-flex align-center mt-1 ml-6">
            <v-icon icon="mdi-archive-outline" size="x-small" color="success" class="mr-2"></v-icon>
            <span class="text-caption text-disabled">of which in Archivio</span>
            <v-spacer></v-spacer>
            <span class="text-caption text-disabled">{{ fmt(data.r2.archiveBytes) }} · {{ data.r2.archiveCount }} files</span>
          </div>
        </template>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { useDisplay } from "vuetify";
import { getStorageStats } from "@/api/index.js";

// Mobile breakpoint check, drives the dialog's :fullscreen prop
const { mobile } = useDisplay();

const props = defineProps({
  // Dialog visibility (v-model)
  modelValue: { type: Boolean, default: false },
});
defineEmits(["update:modelValue"]);

const loading = ref(false);
const error = ref(null);
const data = ref(null);

// Disk rows (server + proxy) rendered with a used/total progress bar.
const disks = computed(() => [
  { title: "Server disk", icon: "mdi-server", info: data.value?.server },
  { title: "Proxy disk", icon: "mdi-server-network", info: data.value?.proxy },
]);

/** Human-readable bytes (binary units, 2 decimals). */
const fmt = (bytes) => {
  if (bytes == null) return "—";
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const exp = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / Math.pow(1024, exp)).toFixed(2)} ${units[exp]}`;
};

/** Used percentage for a disk bar. */
const pct = (used, total) => (total ? Math.min(100, Math.round((used / total) * 100)) : 0);

/** Bar color shifts to a warning/error tone as the disk fills up. */
const barColor = (p) => (p >= 90 ? "error" : p >= 75 ? "warning" : "primary");

/** Fetch the consolidated storage report. */
const load = async () => {
  loading.value = true;
  error.value = null;
  try {
    const res = await getStorageStats();
    data.value = res.data || res;
  } catch (e) {
    error.value = e.message || "Failed to load storage report";
  } finally {
    loading.value = false;
  }
};

// Load (or refresh) whenever the dialog opens.
watch(
  () => props.modelValue,
  (open) => {
    if (open) load();
  },
);
</script>
